import { useSelector } from 'react-redux';
import { useWizard } from 'react-use-wizard';

import { selectClient } from '@client/model/selectors';

import {
  CcrcTypeAProductOptions,
  computeCcrcPricing,
  useCcrcTypeAProducts,
  type CcrcCoverageType,
} from '@/features/ccrc-type-a';
import { useAddPolicyFromQuote } from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

import { QuotesLoadingState, QuotesErrorState } from '../quotes-ui-states';

export function CcrcTypeASelectionStep() {
  const { nextStep } = useWizard();
  const client = useSelector(selectClient);

  const { products, isLoading, isError } = useCcrcTypeAProducts();
  const { selectedQuote, addCcrcTypeAProduct } = useAddPolicyFromQuote();

  const partner = client?.mutableClientPartner ?? null;
  const hasProducts = products.length > 0;

  const handleSelect = (product: CcrcTypeAProduct, coverage: CcrcCoverageType) => {
    if (!client) {
      return;
    }

    const pricing = computeCcrcPricing({
      product,
      coverage,
      clientAge: client.intakeSurvey.clientAge,
      partnerAge: partner?.intakeSurvey.clientAge,
      hasPartner: partner != null,
    });

    addCcrcTypeAProduct({ product, coverage, pricing });
    nextStep();
  };

  return (
    <StepLayout
      title="Your CCRC Options"
      subtitle="Here are your available CCRC Type A plans. Pick one to see how it fits your long-term care plan."
      onNext={() => nextStep()}
      isNextDisabled={!selectedQuote?.ccrcTypeAProduct}
    >
      {isLoading && <QuotesLoadingState />}

      {!isLoading && (isError || !hasProducts) && <QuotesErrorState />}

      {!isLoading && hasProducts && client && (
        <CcrcTypeAProductOptions
          products={products}
          client={client}
          partner={partner}
          onSelect={handleSelect}
        />
      )}
    </StepLayout>
  );
}
